import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Trash2 } from 'lucide-react';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@design-system';

import { useWizardDistributionPartnerContactExclusions } from './use-wizard-distribution-partner-contact-exclusions';
import { PartnerContactExclusionConfirmDialog } from './partner-contact-exclusion-confirm-dialog';

const MAX_NOTE_LENGTH = 100;

const formSchema = z.object({
  email: z.string().trim().toLowerCase().email({ message: 'A valid email is required' }),
  note: z
    .string()
    .trim()
    .max(MAX_NOTE_LENGTH, {
      message: `Note must be ${MAX_NOTE_LENGTH} characters or fewer`,
    })
    .optional(),
});

type FormValues = z.infer<typeof formSchema>;

export function PartnerContactExclusionAdmin() {
  const { entries, isLoading, addEntry, isAdding, removeEntry, isRemoving } =
    useWizardDistributionPartnerContactExclusions();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', note: '' },
  });
  const noteLength = form.watch('note')?.length ?? 0;

  // Adding is destructive (revokes existing connections + already-shared clients), so validate the
  // form, then confirm before mutating.
  const [pending, setPending] = useState<FormValues | null>(null);

  const onSubmit = (values: FormValues) => setPending(values);

  const confirmAdd = () => {
    if (!pending) return;

    addEntry(
      { email: pending.email, note: pending.note?.trim() || null },
      {
        onSuccess: () => {
          form.reset({ email: '', note: '' });
          setPending(null);
        },
      },
    );
  };

  return (
    <Card className="max-w-5xl">
      <CardHeader>
        <CardTitle>Distribution Partners Exclude List</CardTitle>
        <CardDescription className="text-foreground">
          Remove a specific person from the Distribution Partners contacts list by their exact
          email, so no advisor can see, connect to, or share clients with them. Adding also revokes
          any existing connections and already-shared clients to them — removing them later does not
          restore those.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          {isLoading && <p className="text-muted-foreground text-sm">Loading…</p>}
          {!isLoading && entries.length === 0 && (
            <p className="text-muted-foreground text-sm">No one on the exclude list yet.</p>
          )}
          {entries.map(entry => (
            <div
              key={entry.id}
              className="flex items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0">
                <p className="text-foreground truncate text-sm font-medium">{entry.email}</p>
                {entry.note && (
                  <p
                    className="text-muted-foreground truncate text-xs"
                    onMouseEnter={event => {
                      event.currentTarget.title =
                        event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                          ? entry.note || ''
                          : '';
                    }}
                  >
                    {entry.note}
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                disabled={isRemoving}
                aria-label={`Remove ${entry.email} from exclude list`}
                onClick={() => removeEntry(entry.id)}
                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
              >
                <Trash2 />
              </Button>
            </div>
          ))}
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-3 border-t pt-6"
          >
            <p className="text-foreground text-sm font-medium">Add an email</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-start">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="sm:col-span-5">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="person@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem className="sm:col-span-5">
                    <FormLabel>Note (optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Why they're excluded"
                        maxLength={MAX_NOTE_LENGTH}
                        {...field}
                      />
                    </FormControl>
                    <p className="text-muted-foreground text-xs">
                      {noteLength} / {MAX_NOTE_LENGTH} characters
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="sm:col-span-2 sm:pt-6">
                <Button
                  type="submit"
                  disabled={isAdding}
                >
                  Add
                </Button>
              </div>
            </div>
          </form>
        </Form>

        <PartnerContactExclusionConfirmDialog
          open={pending !== null}
          email={pending?.email ?? ''}
          isAdding={isAdding}
          onConfirm={confirmAdd}
          onCancel={() => setPending(null)}
        />
      </CardContent>
    </Card>
  );
}
